<script setup lang="ts">
import { useDefaultStore } from "./store";
import { useModalStore } from "./store/modal";
import ModernModal from "./components/shared/modal/ModernModal.vue";
import { useSessionStore } from "./store/session";
import router from "./router";
import { i18n } from "./i18n";
import { getLocalStorageReac } from "./assets/js/helpers";
import { defaultOptions, usePrimeVue } from "@primevue/core";
import fr from "./assets/js/locale/fr";
import en from "./assets/js/locale/en";
import dayjs from "dayjs";
const store = useDefaultStore();
const sessionStore = useSessionStore();
const modalStore = useModalStore();
const route = useRoute();
const appBaseTitle = import.meta.env.VITE_APP_APP_NAME;
const primevue = usePrimeVue();

const enLocale = {
	...defaultOptions.locale,
	...en,
};

function setLanguage(val: string) {
	if (!val) {
		val = "fr";
	}
	if (!["fr", "en"].includes(val)) {
		val = "fr";
	}
	const pv_locale = val === "fr" ? fr : enLocale;
	dayjs.locale(val);
	i18n.global.locale.value = val;
	primevue.config.locale = pv_locale;
}

const lang = getLocalStorageReac("lang", false, "fr");

watch(
	() => lang.value,
	(val) => {
		setLanguage(val || "fr");
	},
	{ immediate: true },
);

const modernModalRef = ref<InstanceType<typeof ModernModal> | null>(null);
const isAppLoading = ref(true);
const appMetaTitle = computed(() => {
	const name = route.meta.name as string;
	if (name) {
		return `${appBaseTitle} - ${(name || "")?.toString()}`;
	}
	return appBaseTitle;
});

watch(
	() => store.isMobileView,
	(val) => {
		if (val) document.body.classList.add("is-mobile");
		else if (!val) document.body.classList.remove("is-mobile");
	},
);

const canonicalLink = computed(() => {
	const baseUrl = `https://${appBaseTitle}.com`;
	return baseUrl + route.path;
});

function onCloseModalComp() {
	if (modernModalRef.value) {
		modernModalRef.value.onClickClose(null, true);
	} else {
		console.error("No modal instance");
	}
}

function handleClose() {
	modalStore.isGlobalModalVisible = null;
	if (modalStore.globalModal.config.slotProps?.onCloseModal) {
		modalStore.globalModal.config.slotProps.onCloseModal();
	}
}

const isImpersonating = computed(() => {
	return sessionStore.isImpersonating;
});

const forceActive = ref(false);
watch(isImpersonating, (val) => {
	if (val) {
		forceActive.value = true;
		setTimeout(() => {
			forceActive.value = false;
		}, 5000);
	}
});
onMounted(async () => {
	store.setupSettings();
	try {
		isAppLoading.value = true;
		await router.isReady();
		const timeoutId = setTimeout(() => {
			isAppLoading.value = false;
		}, 15_000);
		await sessionStore.loadSessionToken();
		clearTimeout(timeoutId);
		isAppLoading.value = false;
		// onSuccess
	} catch (e) {
		console.log(e);
	} finally {
		isAppLoading.value = false;
	}
});

function handleStopImpersonate() {
	sessionStore.cancelImpersonate();
	router.push({ name: "admin.schools" });
}
</script>

<template>
	<teleport to="head">
		<title>{{ appMetaTitle }}</title>
		<link
			rel="canonical"
			:href="canonicalLink"
		/>
	</teleport>
	<spinner-block
		v-if="isAppLoading"
		background="#0000001a"
		color1="#f6a712"
		color2="black"
	></spinner-block>
	<template v-else>
		<div
			v-if="isImpersonating"
			class="impersonate"
			:class="{ active: forceActive }"
		>
			IMPERSONATING {{ sessionStore.userData?.name }}
			<button
				class="impersonate-button"
				@click="handleStopImpersonate"
			>
				STOP
			</button>
		</div>
		<div class="outer">
			<router-view
				class="main-application-class"
				:class="{ 'is-mobile': store.isMobileToggled }"
			></router-view>
		</div>
	</template>
	<enon-browser-detect></enon-browser-detect>
	<modern-modal
		v-if="modalStore.isGlobalModalVisible"
		:key="modalStore.getModalData('comp')"
		ref="modernModalRef"
		:config="modalStore.getModalData('config', {})"
		:form-style="modalStore.getModalData('formStyle', {})"
		@close-modal="handleClose"
	>
		<template #content="{ slotProps }">
			<!-- eslint-disable vue/v-on-event-hyphenation -->
			<component
				:is="modalStore.getModalData('comp')"
				v-bind="slotProps"
				@close-modal="onCloseModalComp"
				@handle-resize="modernModalRef.setupInitialCoordinates"
			></component>
		</template>
	</modern-modal>
	<popover-modal />
	<p-dynamic-dialog />
</template>

<style lang="scss">
@import "./assets/css/base.css";
@import "https://fonts.googleapis.com/css2?family=Anton&family=Bakbak+One&display=swap";
@import "https://fonts.googleapis.com/css2?family=Open+Sans:wght@100;200;300;400;500;600;700;800&display=swap";
// @import "https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap";
@import "https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap";

body {
	display: flex;

	&.is-mobile {
		overflow-y: hidden;
	}
}

#app {
	width: 100vw;
	// height: 100%;
	color: $daidai-dark-blue;
	&::-webkit-scrollbar {
		background-color: $scrollbar-color;
		width: 5px;
		height: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $scrollbar-color-thumb;
		border-radius: 5px;
	}
	.outer {
		display: flex;
		width: 100%;
		justify-content: center;
		background: url("@/assets/image/app-background.png") no-repeat center
			center / cover;
		.main-application-class {
			flex-grow: 1;
			max-width: 1920px;
			background: $white;
			min-height: 100vh;
		}
	}
	.impersonate {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		height: 2rem;
		padding: 0.1rem;
		background: var(--p-primary-500);
		color: $white;
		text-align: center;
		z-index: 99;
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.5rem;
		opacity: 0.2;
		transition: opacity 0.2s ease;
		&.active {
			opacity: 1;
		}
		&:hover {
			opacity: 1;
		}
		.impersonate-button {
			background: var(--p-secondary-500);
			color: $white;
			border: none;
			border-radius: 5px;
			padding: 2px 4px;
			cursor: pointer;
			font-size: 0.75rem;
			&:hover {
				background: var(--p-secondary-600);
			}
		}
	}
}
</style>
